<script>
import { useAlert } from 'dashboard/composables';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';

export default {
  components: {
    EmojiOrIcon,
    NextButton,
    InlineInput,
  },
  props: {
    href: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      required: true,
    },
    emoji: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    showCopy: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    // SiteUp: valor cru pra copiar/tooltip quando `value` é decorado pra
    // exibição (ex.: telefone com bandeira — copiar precisa dar o E.164).
    copyValue: {
      type: String,
      default: '',
    },
  },
  emits: ['update'],
  data() {
    return {
      isEditing: false,
      editValue: '',
    };
  },
  methods: {
    async onCopy(e) {
      e.preventDefault();
      await copyTextToClipboard(this.copyValue || this.value);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    startEditing() {
      if (!this.editable) return;
      this.editValue = this.value || '';
      this.isEditing = true;
      this.$nextTick(() => {
        this.$refs.editInput?.focus();
      });
    },
    saveEdit() {
      if (!this.isEditing) return;
      this.isEditing = false;
      const trimmed = this.editValue.trim();
      if (trimmed !== (this.value || '')) {
        this.$emit('update', trimmed);
      }
    },
    cancelEdit() {
      this.isEditing = false;
    },
  },
};
</script>

<template>
  <!-- SITEUP V2 / G2: linha kv densa (rótulo muted · valor 13.5) -->
  <div class="group/row mb-2 w-full last:mb-0">
    <!-- Inline edit mode -->
    <div v-if="isEditing" class="flex items-center gap-2">
      <span
        v-if="title"
        class="min-w-0 max-w-[46%] shrink truncate text-[12.5px] text-n-slate-11"
      >
        {{ title }}
      </span>
      <InlineInput
        ref="editInput"
        v-model="editValue"
        :placeholder="title"
        class="!w-fit min-w-0 flex-1"
        @enter-press="saveEdit"
        @escape-press="cancelEdit"
        @blur="saveEdit"
      />
    </div>

    <!-- SiteUp V2 (2026-08-11): VALOR primeiro, rótulo embaixo.
         Antes era rótulo à esquerda e valor à direita, com o rótulo limitado a
         46% da linha. Num painel de 320px isso cortava os dois ao mesmo tempo:
         "Endereço de e-ma" ao lado de "douglas…". O dado é o que importa, então
         ele fica com a linha inteira e o rótulo vira legenda.

         Um elemento só, `<a>` ou `<div>` conforme haja destino. Antes eram dois
         ramos gêmeos do template — e foi assim que o botão de copiar acabou
         existindo em apenas um deles. -->
    <component
      :is="href ? 'a' : 'div'"
      v-else
      :href="href || undefined"
      class="flex items-center gap-2"
    >
      <EmojiOrIcon
        :icon="icon"
        :emoji="emoji"
        icon-size="14"
        class="mt-0.5 shrink-0 self-start opacity-70"
      />
      <span class="flex min-w-0 flex-1 flex-col">
        <span
          v-if="value"
          v-dompurify-html="value"
          class="min-w-0 truncate text-[13.5px] font-medium leading-snug text-n-slate-12"
          :class="{ 'group-hover/row:underline': href }"
          :title="copyValue || value"
        />
        <span v-else class="text-[13.5px] leading-snug text-n-slate-11">
          {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
        </span>
        <span
          v-if="title"
          class="min-w-0 truncate text-[11.5px] leading-snug text-n-slate-11"
        >
          {{ title }}
        </span>
      </span>
      <NextButton
        v-if="showCopy"
        ghost
        xs
        slate
        class="shrink-0 opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100 [@media(hover:none)]:opacity-100"
        icon="i-ri-clipboard-line"
        @click="onCopy"
      />
      <NextButton
        v-if="editable"
        ghost
        xs
        slate
        class="shrink-0 opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100 [@media(hover:none)]:opacity-100"
        icon="i-ri-pencil-line"
        @click.prevent="startEditing"
      />
    </component>
  </div>
</template>
